import React, { useState, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { blockGenerationService } from '../../services/blockGenerationService';
import { FlatlineDetectorBadge } from './FlatlineDetectorBadge';

interface BlockGenerationChartProps {
  plantId?: string;
  dateRange?: string;
  viewMode: 'generation' | 'cuf';
}

export const BlockGenerationChart: React.FC<BlockGenerationChartProps> = React.memo(({
  plantId,
  dateRange = '2024-10-05_to_2024-10-11',
  viewMode = 'generation'
}) => {
  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [blocks, setBlocks] = useState<string[]>([]);

  useEffect(() => {
    loadData();
  }, [plantId, dateRange, viewMode]);

  const loadData = async () => {
    try {
      setLoading(true);
      
      const rawData = await blockGenerationService.getDailyGeneration(plantId, dateRange);
      
      const chartData = Object.entries(rawData).map(([date, blockData]) => {
        const item: any = { date: new Date(date).toLocaleDateString() };
        
        Object.entries(blockData).forEach(([blockId, values]) => {
          item[blockId] = viewMode === 'generation' ? values.generation_kwh : values.cuf;
          item[`${blockId}_flatline`] = values.is_flatline;
        });
        
        return item;
      });

      const allBlocks = new Set<string>();
      Object.values(rawData).forEach(blockData => {
        Object.keys(blockData).forEach(blockId => allBlocks.add(blockId));
      });

      setData(chartData);
      setBlocks(Array.from(allBlocks).sort());
    } catch (error) {
      console.error('Error loading block generation data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getBarColor = (blockId: string, index: number) => {
    const colors = [
      '#6366f1', '#8b5cf6', '#06b6d4', '#10b981', '#f59e0b',
      '#ef4444', '#ec4899', '#84cc16', '#f97316', '#3b82f6'
    ];
    return colors[index % colors.length];
  };

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg p-3 shadow-lg">
          <p className="text-gray-900 dark:text-gray-100 font-medium mb-2">{label}</p>
          {payload.map((entry: any, index: number) => (
            <div key={index} className="flex items-center gap-2 mb-1">
              <div 
                className="w-3 h-3 rounded-full" 
                style={{ backgroundColor: entry.color }}
              />
              <span className="text-gray-700 dark:text-gray-300 text-sm">{entry.dataKey}:</span>
              <span className="text-gray-900 dark:text-gray-100 font-semibold text-sm">
                {entry.value?.toFixed(1)} {viewMode === 'generation' ? 'kWh' : '%'}
              </span>
              {entry.payload[`${entry.dataKey}_flatline`] && (
                <FlatlineDetectorBadge severity="medium" />
              )}
            </div>
          ))}
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <div className="h-96 flex items-center justify-center">
        <div className="animate-pulse bg-gray-200 dark:bg-gray-700 w-full h-full rounded-lg"></div>
      </div>
    );
  }

  return (
    <div className="w-full h-96 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} margin={{ top: 15, right: 15, left: 15, bottom: 15 }}>
          <CartesianGrid strokeDasharray="2 2" className="opacity-30" stroke="currentColor" />
          <XAxis 
            dataKey="date" 
            axisLine={false}
            tickLine={false}
            tick={{ fontSize: 11, fill: 'currentColor' }}
            className="text-gray-600 dark:text-gray-400"
          />
          <YAxis 
            axisLine={false}
            tickLine={false}
            tick={{ fontSize: 11, fill: 'currentColor' }}
            className="text-gray-600 dark:text-gray-400"
          />
          <Tooltip 
            content={<CustomTooltip />} 
            cursor={{ fill: 'rgba(59, 130, 246, 0.1)' }} 
          />
          {blocks.map((blockId, index) => (
            <Bar
              key={blockId}
              dataKey={blockId}
              fill={getBarColor(blockId, index)}
              radius={[3, 3, 0, 0]}
              animationDuration={500}
            />
          ))}
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
});